import { useEffect, useRef } from "react";
import { LoanOfficerDetail } from "pages/application/applicationService";
import { trackFSLoProfileCardRendered, LoProfileCardSurface } from "utilities/full-story";
import { getLoDisplayFields, getLoPhoneType, getLoSalesChannel, isCallQueueOrEmptyLoanOfficer } from "utilities/loanOfficerDisplay";

export interface UseLoProfileCardTrackingParams {
  readonly surface: LoProfileCardSurface;
  readonly loInfo?: LoanOfficerDetail | null;
  readonly isLoading?: boolean;
  readonly error?: unknown;
  readonly headshotLoadError?: boolean;
  readonly dealId?: string | null;
  /** True when the currently displayed loInfo came from the persisted cache rather than a fresh BFF response. */
  readonly cacheHit?: boolean;
}

/*
 * Fires the `lo_profile_card_rendered` FullStory event 
 * render across all 4 LO card surfaces (dashboard, application, registration, form)
*/
export const useLoProfileCardTracking = ({
  surface,
  loInfo,
  isLoading = false,
  error,
  headshotLoadError = false,
  dealId,
  cacheHit = false,
}: UseLoProfileCardTrackingParams) => {
  const lastFiredKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const isSkeleton = isLoading && !loInfo;
    if (isSkeleton) return;

    const hasError = !!error;
    const isCallQueue = isCallQueueOrEmptyLoanOfficer(loInfo);
    const hasLoData = !!loInfo && !isCallQueue;
    const { title, email, phone } = getLoDisplayFields(loInfo);
    const channel = getLoSalesChannel(loInfo);
    const phonetype = getLoPhoneType(loInfo?.phones?.[0]?.type);
    const dataSource = loInfo?.dataSource ?? null;
    const featureFlag: "on" | "off" = dataSource === "controlPlane" ? "on" : "off";

    let bffStatus: "error" | "200" | "204";
    if (hasError) {
      bffStatus = "error";
    } else if (hasLoData) {
      bffStatus = "200";
    } else {
      bffStatus = "204";
    }

    let outcome: "error" | "empty" | "staleCache" | "success";
    if (hasError) {
      outcome = "error";
    } else if (!hasLoData) {
      outcome = "empty";
    } else if (isLoading) {
      outcome = "staleCache";
    } else {
      outcome = "success";
    }

    const dedupeKey = JSON.stringify({ surface, dealId, fullName: loInfo?.fullName, isCallQueue, hasError, outcome });
    if (lastFiredKeyRef.current === dedupeKey) return;
    lastFiredKeyRef.current = dedupeKey;

    trackFSLoProfileCardRendered({
      loFullName: loInfo?.fullName ?? null,
      loTitle: title ?? null,
      loNmls: loInfo?.nmlsLicenseNumber ?? null,
      loEmail: email ?? null,
      loPhone: phone ?? null,
      channel: channel ?? null,
      phonetype: phonetype ?? null,
      hasHeadshot: !!loInfo?.photoImageUrl && !headshotLoadError,
      headshotUrl: loInfo?.photoImageUrl ?? null,
      headshotLoadError,
      surface,
      dataSource,
      isCallQueue,
      featureFlag,
      hasLoData,
      dealId: dealId ?? null,
      bffStatus,
      outcome,
      cacheHit,
    });
  }, [surface, loInfo, isLoading, error, headshotLoadError, dealId, cacheHit]);
};
